import { type ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTenantExists } from "@/hooks/queries/auth/useTenantExists";
import { Spinner } from "@/components/common/Spinner";

type Props = { children: ReactNode };

/**
 * Guards any ``/:tenantSlug/*`` route by verifying the slug exists in the DB
 * before rendering auth forms or the authenticated app tree.
 *
 * - Loading  → ``<Spinner size="lg" />`` (matches RequireAuth loading state)
 * - Exists   → render children
 * - Not found → ``<Navigate to="/404" replace />`` (NotFound shows workspace CTA)
 *
 * Wraps ONLY slug-scoped routes (``/:tenantSlug/*``). Slug-less routes
 * (``/login``, ``/auth/signup``, ``/verify``, ``/404``, ``/__mocks/*``,
 * ``/dev/primitives``) are NOT wrapped.
 */
export function RequireValidSlug({ children }: Props): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { isLoading, data } = useTenantExists(tenantSlug);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!data?.exists) {
    return <Navigate to="/404" replace />;
  }

  return <>{children}</>;
}
